/**
 * Helper to call back element then rendered in DOM
 *
 * @param selector Query Selector
 * @param callback Function called on finding element after animation frame
 * @returns {*|boolean}
 */
function waitForElement(selector, callback) {
  const el = document.querySelector(selector);
  if (el) {
    return callback(el);
  }

  /* eslint-disable no-restricted-globals */
  requestAnimationFrame(() => waitForElement(selector, callback));

  return true;
}

/**
 * Helper that set specific style overrides to legend panel
 *
 * @param view View object, that holds all elements
 * @param button Legend button that toggle legend content panel
 */
function setLegendPanelStyles(view, button) {
  const legendContentContainer = document.querySelector('#legend_controls_content');
  const legendPanelContainer = document.querySelector('.esri-legend.esri-widget.esri-widget--panel');
  const viewHeight = view.container.clientHeight;
  const viewRect = view.container.getBoundingClientRect();
  const buttonRect = button.getBoundingClientRect();
  const offsetTop = buttonRect.top - viewRect.top;
  const pHeight = legendContentContainer.offsetHeight;
  const translateY = (-pHeight / 2) - offsetTop;
  const maxHeight = viewHeight / 2;

  if (legendContentContainer && legendPanelContainer) {
    legendContentContainer.style.height = `${pHeight}px`;
    legendContentContainer.style.maxHeight = `${maxHeight}px`;
    legendContentContainer.style.top = '50%';
    legendContentContainer.style.right = '38px';
    legendContentContainer.style.transform = `translateY(${translateY}px)`;

    legendPanelContainer.style.boxShadow = '0 0 25px rgba(0, 0, 0, 0.25)';
  }
}

/**
 * Overwrite Styles on home and zoom icons in view area, because they are in the shadow-container
 *
 * @param view
 */
export function customViewMapStyles(view) {
  const homeWidget = view.ui.find('home');
  const zoomWidget = view.ui.find('zoom');
  const zoomButtonsWidget = zoomWidget.container.getElementsByTagName('calcite-button');

  const homeButtonWidget = homeWidget.container.getElementsByTagName('calcite-button')[0];
  const homeButtonWidgetShadow = homeButtonWidget.shadowRoot;
  const shadowHomeButtonElement = homeButtonWidgetShadow.querySelector('button');
  const shadowHomeIconElement = homeButtonWidgetShadow.querySelector('calcite-icon');
  const shadowHomeIconShadow = shadowHomeIconElement.shadowRoot;
  const homeSvgElement = shadowHomeIconShadow.querySelector('svg');

  homeSvgElement.style.color = '#9e007e';
  shadowHomeIconElement.style.color = '#9e007e';

  shadowHomeButtonElement.style.backgroundColor = 'transparent';
  shadowHomeButtonElement.style.color = 'none';
  shadowHomeButtonElement.style.color = '#9e007e';
  shadowHomeButtonElement.style.fontWeight = '700';

  shadowHomeButtonElement.addEventListener('mouseover', () => {
    homeSvgElement.style.color = '#ffffff';
    shadowHomeIconElement.style.color = '#ffffff';
    shadowHomeButtonElement.style.color = '#ffffff';
  });

  shadowHomeButtonElement.addEventListener('mouseleave', () => {
    homeSvgElement.style.color = '#9e007e';
    shadowHomeIconElement.style.color = '#9e007e';
    shadowHomeButtonElement.style.color = '#9e007e';
  });

  shadowHomeButtonElement.addEventListener('focus', () => {
    shadowHomeButtonElement.style.outline = 'none';
  });

  /**
   * Go through Zoom widget cause there is a zoom in and zoom out icon in it with needs a loop
   */
  Array.from(zoomButtonsWidget).forEach((button) => {

    /**
     * check the disabled zoom button and its icons
     */
    if (button.hasAttribute('disabled')) {
      const zoomDisabledWidgetShadow = button.shadowRoot;
      const shadowZoomButtonDisabledElement = zoomDisabledWidgetShadow.querySelector('button');
      const shadowDisabledZoomIconElement = zoomDisabledWidgetShadow.querySelector('button calcite-icon');
      const shadowDisabledZoomIconShadow = shadowDisabledZoomIconElement.shadowRoot;
      const zoomDisabledSvgElement = shadowDisabledZoomIconShadow.querySelector('svg');
      zoomDisabledSvgElement.style.color = 'none';
      shadowZoomButtonDisabledElement.style.color = '#9e007e';
      /* eslint-disable no-param-reassign */
      button.style.opacity = '1';

      button.addEventListener('mouseover', () => {
        zoomDisabledSvgElement.style.color = '#ffffff';
        shadowZoomButtonDisabledElement.style.color = '#ffffff';
      });

      button.addEventListener('mouseleave', () => {
        zoomDisabledSvgElement.style.color = '#9e007e';
        shadowZoomButtonDisabledElement.style.color = '#9e007e';
      });

      shadowZoomButtonDisabledElement.addEventListener('focus', () => {
        shadowZoomButtonDisabledElement.style.outline = 'none';
      });
    }

    /**
     * Then the active zoom button and icon
     *
     * @type {ShadowRoot}
     */
    const zoomWidgetShadow = button.shadowRoot;
    const shadowZoomButtonElement = zoomWidgetShadow.querySelector('button');
    const shadowZoomIconElement = shadowZoomButtonElement.querySelector('calcite-icon');
    const shadowZoomIconShadow = shadowZoomIconElement.shadowRoot;
    const zoomSvgElement = shadowZoomIconShadow.querySelector('svg');

    zoomSvgElement.style.color = '#9e007e';
    shadowZoomButtonElement.style.backgroundColor = 'transparent';
    shadowZoomButtonElement.style.fontWeight = '700';

    shadowZoomButtonElement.addEventListener('mouseover', () => {
      zoomSvgElement.style.color = '#ffffff';
      shadowZoomButtonElement.style.color = '#ffffff';
    });

    shadowZoomButtonElement.addEventListener('mouseleave', () => {
      zoomSvgElement.style.color = '#9e007e';
      shadowZoomButtonElement.style.color = '#9e007e';
    });

    shadowZoomButtonElement.addEventListener('focus', () => {
      shadowZoomButtonElement.style.outline = 'none';
    });
  });
}

/**
 * Waiting for Element is loaded and add dynamic styles also within shadow-root of elements
 *
 * @param view
 */
export function addLegendStylesAfterRender(view) {
  waitForElement('.esri-widget .esri-expand__toggle .esri-widget--button', (button) => {
    const buttonElement = button;
    const legendWidget = view.ui.find('legend');

    /**
     * This is needed to wait also for the inner button of the shadow root
     */
    requestAnimationFrame(function find() {
      const btn = legendWidget.container.getElementsByTagName('calcite-action')[0];

      if (!btn || !btn.shadowRoot) {
        return requestAnimationFrame(find);
      }

      const internal = btn.shadowRoot.querySelector('button');
      const internalIcon = btn.querySelector('calcite-icon');

      if (!internal || !internalIcon) {
        return requestAnimationFrame(find);
      }

      internal.style.backgroundColor = 'transparent';
      internal.style.color = '#9e007e';
      internal.style.fontWeight = '700';

      internal.addEventListener('mouseover', () => {
        internalIcon.style.color = '#ffffff';
        internal.style.color = '#ffffff';
      });

      internal.addEventListener('mouseleave', () => {
        internalIcon.style.color = '#9e007e';
        internal.style.color = '#9e007e';
      });

      internal.addEventListener('focus', () => {
        internal.style.outline = 'none';
      });

      return true;
    });

    /**
     * Style outside rendered calcite button after rendered
     *
     * @type {string}
     */
    buttonElement.style.color = '#9e007e';
    buttonElement.style.fontWeight = '700';

    buttonElement.addEventListener('mouseover', () => {
      buttonElement.style.backgroundColor = '#9e007e';
      buttonElement.style.color = '#ffffff';
    });

    buttonElement.addEventListener('mouseleave', () => {
      buttonElement.style.backgroundColor = '#ffffff';
      buttonElement.style.color = '#9e007e';
    });

    buttonElement.addEventListener('click', () => {
      setLegendPanelStyles(view, buttonElement);
    });

    view.container.addEventListener('resize', () => {
      setLegendPanelStyles(view, buttonElement);
    });
  });
}
